/**
 * WorkspaceDesigner - WYSIWYG workspace definition builder.
 *
 * Provides a visual editor for workspace JSON definitions with:
 * - View tabs management (add/remove/reorder views)
 * - Per-view editor with sections for:
 *   - Header: search filters, toolbar config
 *   - Master grid: entity picker, field selection, action config
 *   - Detail panel: child entity config
 *   - Filter chips editor
 *   - Status bar counters
 * - Entity/field catalog integration via /api/v1/catalog
 * - Live preview of the generated JSON
 */

import React, { useState, useEffect, useCallback } from 'react';
import Editor from '@monaco-editor/react';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Switch,
  Tooltip,
  Tabs,
  Tab,
  Divider,
  Autocomplete,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Paper,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  DragIndicator as DragIcon,
  ArrowUpward as UpIcon,
  ArrowDownward as DownIcon,
  Code as CodeIcon,
  Visibility as PreviewIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  ViewColumn as ColumnIcon,
  TableChart as GridIcon,
  Assessment as StatusBarIcon,
  Settings as ToolbarIcon,
} from '@mui/icons-material';

const API_BASE = process.env.REACT_APP_API_BASE || '';
const getAccessToken = () => localStorage.getItem('waide_access_token');

// Color options for filter chips
const CHIP_COLORS = ['default', 'primary', 'info', 'warning', 'success', 'error'];

// ─── Entity Picker (Autocomplete that searches catalog) ─────────────────

const EntityPicker = ({ value, onChange, colors, label = 'Entity' }) => {
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE}/api/v1/catalog/entities`, {
          headers: { Authorization: `Bearer ${getAccessToken()}` },
        });
        if (res.ok) {
          const data = await res.json();
          setEntities(data.entities || []);
        }
      } catch (err) {
        console.error('Failed to load entities:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const selected = entities.find((e) => e.entityName === value) || null;

  return (
    <Autocomplete
      size="small"
      options={entities}
      getOptionLabel={(opt) => `${opt.displayName || opt.entityName} (${opt.entityName})`}
      groupBy={(opt) => opt.category || 'Other'}
      value={selected}
      onChange={(_, newVal) => onChange(newVal?.entityName || '')}
      loading={loading}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          InputLabelProps={{ sx: { color: colors.textSecondary } }}
          InputProps={{
            ...params.InputProps,
            sx: { color: colors.textPrimary, bgcolor: colors.background },
          }}
        />
      )}
      renderOption={(props, opt) => (
        <li {...props} key={opt.entityName}>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {opt.displayName || opt.entityName}
            </Typography>
            <Typography variant="caption" sx={{ color: '#888' }}>
              {opt.entityName} &middot; {opt.dataSource} &middot; {opt.domain}
            </Typography>
          </Box>
        </li>
      )}
      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
    />
  );
};

// ─── Function Picker ────────────────────────────────────────────────────

const FunctionPicker = ({ entityName, value, onChange, colors }) => {
  const [functions, setFunctions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!entityName) {
      setFunctions([]);
      return;
    }
    const load = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE}/api/v1/catalog/${entityName}/functions`, {
          headers: { Authorization: `Bearer ${getAccessToken()}` },
        });
        if (res.ok) {
          const data = await res.json();
          setFunctions(data.functions || []);
        }
      } catch (err) {
        console.error('Failed to load functions:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [entityName]);

  return (
    <FormControl size="small" fullWidth>
      <InputLabel sx={{ color: colors.textSecondary }}>Function</InputLabel>
      <Select
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        label="Function"
        disabled={!entityName || loading}
        sx={{ color: colors.textPrimary, bgcolor: colors.background, borderRadius: 0 }}
      >
        {functions.map((fn) => (
          <MenuItem key={fn} value={fn}>{fn}</MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

// ─── Field Selector (multi-select from entity field metadata) ───────────

const FieldSelector = ({ entityName, selectedFields, onChange, colors }) => {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!entityName) {
      setFields([]);
      return;
    }
    const load = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE}/api/v1/catalog/${entityName}/fields`, {
          headers: { Authorization: `Bearer ${getAccessToken()}` },
        });
        if (res.ok) {
          const data = await res.json();
          setFields(data.fields || []);
        }
      } catch (err) {
        console.error('Failed to load fields:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [entityName]);

  if (!entityName) return null;

  return (
    <Box>
      <Typography variant="caption" sx={{ color: colors.textMuted, mb: 0.5, display: 'block' }}>
        Available Fields ({fields.length})
      </Typography>
      {loading ? (
        <CircularProgress size={20} sx={{ color: '#8b5cf6' }} />
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {fields.map((f) => {
            const isSelected = selectedFields?.includes(f.fieldName);
            return (
              <Chip
                key={f.fieldName}
                label={`${f.displayLabel} (${f.fieldName})`}
                size="small"
                onClick={() => {
                  if (isSelected) {
                    onChange(selectedFields.filter((n) => n !== f.fieldName));
                  } else {
                    onChange([...(selectedFields || []), f.fieldName]);
                  }
                }}
                sx={{
                  bgcolor: isSelected ? '#8b5cf630' : 'transparent',
                  color: isSelected ? '#8b5cf6' : colors.textSecondary,
                  border: `1px solid ${isSelected ? '#8b5cf6' : colors.border}`,
                  fontWeight: isSelected ? 600 : 400,
                  fontSize: '0.7rem',
                  cursor: 'pointer',
                }}
              />
            );
          })}
        </Box>
      )}
    </Box>
  );
};

// ─── Filter Chips Editor ────────────────────────────────────────────────

const FilterChipsEditor = ({ chips = [], onChange, entityName, colors }) => {
  const addChip = () => {
    onChange([...chips, { label: '', field: '', value: '', color: 'default', default: false }]);
  };

  const updateChip = (index, key, value) => {
    const updated = chips.map((c, i) => (i === index ? { ...c, [key]: value } : c));
    onChange(updated);
  };

  const removeChip = (index) => {
    onChange(chips.filter((_, i) => i !== index));
  };

  return (
    <Box>
      {chips.map((chip, idx) => (
        <Box
          key={idx}
          sx={{
            display: 'flex', gap: 1, alignItems: 'center', mb: 1,
            p: 1, border: `1px solid ${colors.border}`, bgcolor: colors.background,
          }}
        >
          <TextField
            size="small"
            label="Label"
            value={chip.label}
            onChange={(e) => updateChip(idx, 'label', e.target.value)}
            sx={{ flex: 1 }}
            InputProps={{ sx: { color: colors.textPrimary, borderRadius: 0 } }}
            InputLabelProps={{ sx: { color: colors.textSecondary } }}
          />
          <TextField
            size="small"
            label="Field"
            value={chip.field}
            onChange={(e) => updateChip(idx, 'field', e.target.value)}
            sx={{ flex: 1 }}
            InputProps={{ sx: { color: colors.textPrimary, borderRadius: 0 } }}
            InputLabelProps={{ sx: { color: colors.textSecondary } }}
          />
          <TextField
            size="small"
            label="Value"
            value={chip.value}
            onChange={(e) => updateChip(idx, 'value', e.target.value)}
            sx={{ flex: 1 }}
            InputProps={{ sx: { color: colors.textPrimary, borderRadius: 0 } }}
            InputLabelProps={{ sx: { color: colors.textSecondary } }}
          />
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel sx={{ color: colors.textSecondary }}>Color</InputLabel>
            <Select
              value={chip.color}
              onChange={(e) => updateChip(idx, 'color', e.target.value)}
              label="Color"
              sx={{ color: colors.textPrimary, borderRadius: 0 }}
            >
              {CHIP_COLORS.map((c) => (
                <MenuItem key={c} value={c}>{c}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={chip.default || false}
                onChange={(e) => updateChip(idx, 'default', e.target.checked)}
              />
            }
            label={<Typography variant="caption" sx={{ color: colors.textSecondary }}>Default</Typography>}
          />
          <IconButton size="small" onClick={() => removeChip(idx)} sx={{ color: '#ef4444' }}>
            <DeleteIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      ))}
      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={addChip}
        sx={{ color: '#8b5cf6', textTransform: 'none' }}
      >
        Add Filter Chip
      </Button>
    </Box>
  );
};

// ─── Status Bar Counters Editor ─────────────────────────────────────────

const StatusBarEditor = ({ counters = [], onChange, colors }) => {
  const addCounter = () => {
    onChange([...counters, { label: '', type: 'total', field: '', value: '', color: 'default' }]);
  };

  const updateCounter = (index, key, value) => {
    const updated = counters.map((c, i) => (i === index ? { ...c, [key]: value } : c));
    onChange(updated);
  };

  const removeCounter = (index) => {
    onChange(counters.filter((_, i) => i !== index));
  };

  return (
    <Box>
      {counters.map((counter, idx) => (
        <Box
          key={idx}
          sx={{
            display: 'flex', gap: 1, alignItems: 'center', mb: 1,
            p: 1, border: `1px solid ${colors.border}`, bgcolor: colors.background,
          }}
        >
          <TextField
            size="small"
            label="Label"
            value={counter.label}
            onChange={(e) => updateCounter(idx, 'label', e.target.value)}
            sx={{ flex: 1 }}
            InputProps={{ sx: { color: colors.textPrimary, borderRadius: 0 } }}
            InputLabelProps={{ sx: { color: colors.textSecondary } }}
          />
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel sx={{ color: colors.textSecondary }}>Type</InputLabel>
            <Select
              value={counter.type || 'total'}
              onChange={(e) => updateCounter(idx, 'type', e.target.value)}
              label="Type"
              sx={{ color: colors.textPrimary, borderRadius: 0 }}
            >
              <MenuItem value="total">Total</MenuItem>
              <MenuItem value="field">Field Match</MenuItem>
            </Select>
          </FormControl>
          {counter.type !== 'total' && (
            <>
              <TextField
                size="small"
                label="Field"
                value={counter.field || ''}
                onChange={(e) => updateCounter(idx, 'field', e.target.value)}
                sx={{ flex: 1 }}
                InputProps={{ sx: { color: colors.textPrimary, borderRadius: 0 } }}
                InputLabelProps={{ sx: { color: colors.textSecondary } }}
              />
              <TextField
                size="small"
                label="Value"
                value={counter.value || ''}
                onChange={(e) => updateCounter(idx, 'value', e.target.value)}
                sx={{ flex: 1 }}
                InputProps={{ sx: { color: colors.textPrimary, borderRadius: 0 } }}
                InputLabelProps={{ sx: { color: colors.textSecondary } }}
              />
            </>
          )}
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel sx={{ color: colors.textSecondary }}>Color</InputLabel>
            <Select
              value={counter.color || 'default'}
              onChange={(e) => updateCounter(idx, 'color', e.target.value)}
              label="Color"
              sx={{ color: colors.textPrimary, borderRadius: 0 }}
            >
              {CHIP_COLORS.map((c) => (
                <MenuItem key={c} value={c}>{c}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <IconButton size="small" onClick={() => removeCounter(idx)} sx={{ color: '#ef4444' }}>
            <DeleteIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      ))}
      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={addCounter}
        sx={{ color: '#8b5cf6', textTransform: 'none' }}
      >
        Add Counter
      </Button>
    </Box>
  );
};

// ─── View Editor ────────────────────────────────────────────────────────

const ViewEditor = ({ view, index, onChange, onDelete, colors }) => {
  const update = (key, value) => {
    onChange({ ...view, [key]: value });
  };

  const updateMaster = (key, value) => {
    onChange({ ...view, master: { ...(view.master || {}), [key]: value } });
  };

  const updateDetail = (key, value) => {
    onChange({ ...view, detail: { ...(view.detail || {}), [key]: value } });
  };

  const updateToolbar = (key, value) => {
    onChange({ ...view, toolbar: { ...(view.toolbar || {}), [key]: value } });
  };

  const hasDetail = !!view.detail?.entity;

  return (
    <Box sx={{ border: `1px solid ${colors.border}`, bgcolor: colors.surface, mb: 2 }}>
      {/* View Header */}
      <Box
        sx={{
          display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1,
          borderBottom: `1px solid ${colors.border}`, bgcolor: colors.surfaceElevated || colors.surface,
        }}
      >
        <DragIcon sx={{ fontSize: 18, color: colors.textMuted }} />
        <Typography variant="subtitle2" sx={{ color: '#8b5cf6', fontWeight: 700, flex: 1 }}>
          View {index + 1}: {view.label || '(unnamed)'}
        </Typography>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={view.default || false}
              onChange={(e) => update('default', e.target.checked)}
            />
          }
          label={<Typography variant="caption" sx={{ color: colors.textSecondary }}>Default</Typography>}
        />
        <Tooltip title="Delete view">
          <IconButton size="small" onClick={onDelete} sx={{ color: '#ef4444' }}>
            <DeleteIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ p: 2 }}>
        {/* Basic View Properties */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField
            size="small"
            label="View ID"
            value={view.viewId || ''}
            onChange={(e) => update('viewId', e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
            sx={{ flex: 1 }}
            InputProps={{ sx: { color: colors.textPrimary, borderRadius: 0 } }}
            InputLabelProps={{ sx: { color: colors.textSecondary } }}
            helperText="Unique ID (e.g., receipts, areas)"
          />
          <TextField
            size="small"
            label="Tab Label"
            value={view.label || ''}
            onChange={(e) => update('label', e.target.value)}
            sx={{ flex: 1 }}
            InputProps={{ sx: { color: colors.textPrimary, borderRadius: 0 } }}
            InputLabelProps={{ sx: { color: colors.textSecondary } }}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel sx={{ color: colors.textSecondary }}>Type</InputLabel>
            <Select
              value={view.type || 'grid'}
              onChange={(e) => update('type', e.target.value)}
              label="Type"
              sx={{ color: colors.textPrimary, borderRadius: 0 }}
            >
              <MenuItem value="grid">Grid</MenuItem>
              <MenuItem value="dashboard">Dashboard</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Master Entity Section */}
        <Accordion
          defaultExpanded
          sx={{ bgcolor: colors.background, border: `1px solid ${colors.border}`, '&:before': { display: 'none' } }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: colors.textMuted }} />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <GridIcon sx={{ fontSize: 18, color: '#22c55e' }} />
              <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
                Master Grid
              </Typography>
              {view.master?.entity && (
                <Chip label={view.master.entity} size="small" sx={{ bgcolor: '#22c55e20', color: '#22c55e', fontSize: '0.7rem' }} />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 2 }}>
                  <EntityPicker
                    value={view.master?.entity || ''}
                    onChange={(val) => updateMaster('entity', val)}
                    colors={colors}
                    label="Master Entity"
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <FunctionPicker
                    entityName={view.master?.entity}
                    value={view.master?.function}
                    onChange={(val) => updateMaster('function', val)}
                    colors={colors}
                  />
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  size="small"
                  label="Primary Key Field"
                  value={view.master?.primaryKey || ''}
                  onChange={(e) => updateMaster('primaryKey', e.target.value)}
                  sx={{ flex: 1 }}
                  InputProps={{ sx: { color: colors.textPrimary, borderRadius: 0 } }}
                  InputLabelProps={{ sx: { color: colors.textSecondary } }}
                  helperText="e.g., receiptId, areaId"
                />
                <TextField
                  size="small"
                  label="Display Field"
                  value={view.master?.displayField || ''}
                  onChange={(e) => updateMaster('displayField', e.target.value)}
                  sx={{ flex: 1 }}
                  InputProps={{ sx: { color: colors.textPrimary, borderRadius: 0 } }}
                  InputLabelProps={{ sx: { color: colors.textSecondary } }}
                />
                <TextField
                  size="small"
                  label="Response Data Key"
                  value={view.master?.responseDataKey || ''}
                  onChange={(e) => updateMaster('responseDataKey', e.target.value)}
                  sx={{ flex: 1 }}
                  InputProps={{ sx: { color: colors.textPrimary, borderRadius: 0 } }}
                  InputLabelProps={{ sx: { color: colors.textSecondary } }}
                  helperText="Key in API response (e.g., data, areas)"
                />
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={view.master?.selectable || false}
                    onChange={(e) => updateMaster('selectable', e.target.checked)}
                  />
                }
                label={<Typography variant="body2" sx={{ color: colors.textSecondary }}>Multi-selectable rows</Typography>}
              />
              {/* Empty State */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  size="small"
                  label="Empty State Title"
                  value={view.master?.emptyState?.title || ''}
                  onChange={(e) => updateMaster('emptyState', { ...(view.master?.emptyState || {}), title: e.target.value })}
                  sx={{ flex: 1 }}
                  InputProps={{ sx: { color: colors.textPrimary, borderRadius: 0 } }}
                  InputLabelProps={{ sx: { color: colors.textSecondary } }}
                />
                <TextField
                  size="small"
                  label="Empty State Message"
                  value={view.master?.emptyState?.message || ''}
                  onChange={(e) => updateMaster('emptyState', { ...(view.master?.emptyState || {}), message: e.target.value })}
                  sx={{ flex: 2 }}
                  InputProps={{ sx: { color: colors.textPrimary, borderRadius: 0 } }}
                  InputLabelProps={{ sx: { color: colors.textSecondary } }}
                />
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Detail Entity Section */}
        <Accordion
          sx={{ bgcolor: colors.background, border: `1px solid ${colors.border}`, mt: 1, '&:before': { display: 'none' } }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: colors.textMuted }} />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ColumnIcon sx={{ fontSize: 18, color: '#3b82f6' }} />
              <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
                Detail Panel
              </Typography>
              {hasDetail && (
                <Chip label={view.detail.entity} size="small" sx={{ bgcolor: '#3b82f620', color: '#3b82f6', fontSize: '0.7rem' }} />
              )}
              <Typography variant="caption" sx={{ color: colors.textMuted }}>(optional)</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 2 }}>
                  <EntityPicker
                    value={view.detail?.entity || ''}
                    onChange={(val) => updateDetail('entity', val)}
                    colors={colors}
                    label="Detail Entity"
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <FunctionPicker
                    entityName={view.detail?.entity}
                    value={view.detail?.function}
                    onChange={(val) => updateDetail('function', val)}
                    colors={colors}
                  />
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  size="small"
                  label="Parent Key"
                  value={view.detail?.parentKey || ''}
                  onChange={(e) => updateDetail('parentKey', e.target.value)}
                  sx={{ flex: 1 }}
                  InputProps={{ sx: { color: colors.textPrimary, borderRadius: 0 } }}
                  InputLabelProps={{ sx: { color: colors.textSecondary } }}
                  helperText="Field in master row to pass"
                />
                <TextField
                  size="small"
                  label="Foreign Key"
                  value={view.detail?.foreignKey || ''}
                  onChange={(e) => updateDetail('foreignKey', e.target.value)}
                  sx={{ flex: 1 }}
                  InputProps={{ sx: { color: colors.textPrimary, borderRadius: 0 } }}
                  InputLabelProps={{ sx: { color: colors.textSecondary } }}
                  helperText="Param name in detail API call"
                />
                <TextField
                  size="small"
                  label="Title Template"
                  value={view.detail?.title || ''}
                  onChange={(e) => updateDetail('title', e.target.value)}
                  sx={{ flex: 1 }}
                  InputProps={{ sx: { color: colors.textPrimary, borderRadius: 0 } }}
                  InputLabelProps={{ sx: { color: colors.textSecondary } }}
                  helperText="e.g., Lines for {{receiptId}}"
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  size="small"
                  label="Data Path"
                  value={view.detail?.dataPath || ''}
                  onChange={(e) => updateDetail('dataPath', e.target.value)}
                  sx={{ flex: 1 }}
                  InputProps={{ sx: { color: colors.textPrimary, borderRadius: 0 } }}
                  InputLabelProps={{ sx: { color: colors.textSecondary } }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={view.detail?.embedded || false}
                      onChange={(e) => updateDetail('embedded', e.target.checked)}
                    />
                  }
                  label={<Typography variant="body2" sx={{ color: colors.textSecondary }}>Embedded data</Typography>}
                />
              </Box>
              {hasDetail && (
                <Button
                  size="small"
                  startIcon={<DeleteIcon />}
                  onClick={() => onChange({ ...view, detail: undefined })}
                  sx={{ color: '#ef4444', textTransform: 'none', alignSelf: 'flex-start' }}
                >
                  Remove Detail
                </Button>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Toolbar Section */}
        <Accordion
          sx={{ bgcolor: colors.background, border: `1px solid ${colors.border}`, mt: 1, '&:before': { display: 'none' } }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: colors.textMuted }} />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ToolbarIcon sx={{ fontSize: 18, color: '#f59e0b' }} />
              <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
                Toolbar
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={view.toolbar?.showSearch ?? true}
                      onChange={(e) => updateToolbar('showSearch', e.target.checked)}
                    />
                  }
                  label={<Typography variant="body2" sx={{ color: colors.textSecondary }}>Search</Typography>}
                />
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={view.toolbar?.showRefresh ?? true}
                      onChange={(e) => updateToolbar('showRefresh', e.target.checked)}
                    />
                  }
                  label={<Typography variant="body2" sx={{ color: colors.textSecondary }}>Refresh</Typography>}
                />
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={view.toolbar?.showAdvancedSearch ?? false}
                      onChange={(e) => updateToolbar('showAdvancedSearch', e.target.checked)}
                    />
                  }
                  label={<Typography variant="body2" sx={{ color: colors.textSecondary }}>Advanced Search</Typography>}
                />
              </Box>
              <TextField
                size="small"
                label="Search Placeholder"
                value={view.toolbar?.searchPlaceholder || ''}
                onChange={(e) => updateToolbar('searchPlaceholder', e.target.value)}
                fullWidth
                InputProps={{ sx: { color: colors.textPrimary, borderRadius: 0 } }}
                InputLabelProps={{ sx: { color: colors.textSecondary } }}
              />
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Filter Chips Section */}
        <Accordion
          sx={{ bgcolor: colors.background, border: `1px solid ${colors.border}`, mt: 1, '&:before': { display: 'none' } }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: colors.textMuted }} />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FilterIcon sx={{ fontSize: 18, color: '#06b6d4' }} />
              <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
                Filter Chips
              </Typography>
              {view.filters?.chips?.length > 0 && (
                <Chip
                  label={view.filters.chips.length}
                  size="small"
                  sx={{ bgcolor: '#06b6d420', color: '#06b6d4', fontSize: '0.7rem', height: 20, minWidth: 20 }}
                />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <FilterChipsEditor
              chips={view.filters?.chips || []}
              onChange={(chips) => update('filters', { ...(view.filters || {}), chips })}
              entityName={view.master?.entity}
              colors={colors}
            />
          </AccordionDetails>
        </Accordion>

        {/* Status Bar Section */}
        <Accordion
          sx={{ bgcolor: colors.background, border: `1px solid ${colors.border}`, mt: 1, '&:before': { display: 'none' } }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: colors.textMuted }} />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <StatusBarIcon sx={{ fontSize: 18, color: '#ec4899' }} />
              <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
                Status Bar Counters
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <StatusBarEditor
              counters={view.statusBar?.counters || []}
              onChange={(counters) => update('statusBar', { ...(view.statusBar || {}), counters })}
              colors={colors}
            />
          </AccordionDetails>
        </Accordion>
      </Box>
    </Box>
  );
};

// ─── Main WorkspaceDesigner Component ───────────────────────────────────

const WorkspaceDesigner = ({ workspace, onSave, onClose, colors }) => {
  const [definition, setDefinition] = useState({ views: [] });
  const [activeTab, setActiveTab] = useState(0); // 0 = designer, 1 = JSON preview
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Initialize from workspace
  useEffect(() => {
    if (workspace?.definition) {
      try {
        const parsed = typeof workspace.definition === 'string'
          ? JSON.parse(workspace.definition)
          : workspace.definition;
        setDefinition(parsed || { views: [] });
      } catch {
        setDefinition({ views: [] });
      }
    }
  }, [workspace]);

  const addView = useCallback(() => {
    const viewNum = (definition.views?.length || 0) + 1;
    setDefinition((prev) => ({
      ...prev,
      views: [
        ...(prev.views || []),
        {
          viewId: `view_${viewNum}`,
          label: `View ${viewNum}`,
          type: 'grid',
          default: viewNum === 1,
          master: { entity: '', function: 'List', primaryKey: '' },
          toolbar: { showSearch: true, showRefresh: true },
        },
      ],
    }));
  }, [definition.views?.length]);

  const updateView = useCallback((index, updatedView) => {
    setDefinition((prev) => ({
      ...prev,
      views: prev.views.map((v, i) => (i === index ? updatedView : v)),
    }));
  }, []);

  const deleteView = useCallback((index) => {
    setDefinition((prev) => ({
      ...prev,
      views: prev.views.filter((_, i) => i !== index),
    }));
  }, []);

  const moveView = useCallback((index, direction) => {
    setDefinition((prev) => {
      const views = [...prev.views];
      const swapIdx = direction === 'up' ? index - 1 : index + 1;
      if (swapIdx < 0 || swapIdx >= views.length) return prev;
      [views[index], views[swapIdx]] = [views[swapIdx], views[index]];
      return { ...prev, views };
    });
  }, []);

  // Clean definition for output (remove empty objects, etc.)
  const cleanDefinition = useCallback((def) => {
    const cleaned = { ...def };
    if (cleaned.views) {
      cleaned.views = cleaned.views.map((view) => {
        const v = { ...view };
        // Remove empty detail
        if (v.detail && !v.detail.entity) delete v.detail;
        // Remove empty emptyState
        if (v.master?.emptyState && !v.master.emptyState.title && !v.master.emptyState.message) {
          const m = { ...v.master };
          delete m.emptyState;
          v.master = m;
        }
        // Remove empty filter chips
        if (v.filters?.chips?.length === 0) delete v.filters;
        // Remove empty statusBar
        if (v.statusBar?.counters?.length === 0) delete v.statusBar;
        return v;
      });
    }
    return cleaned;
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      const cleaned = cleanDefinition(definition);
      const defJson = JSON.stringify(cleaned, null, 2);

      const response = await fetch(`${API_BASE}/api/v1/workspaces/manage/${workspace.id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ definition: defJson }),
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      setSuccess('Definition saved');
      if (onSave) onSave();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }, [definition, workspace, onSave, cleanDefinition]);

  const jsonPreview = JSON.stringify(cleanDefinition(definition), null, 2);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: colors.background }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 2, py: 1.5, borderBottom: `1px solid ${colors.border}`, bgcolor: colors.surface,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="subtitle1" sx={{ color: colors.textPrimary, fontWeight: 700 }}>
            Workspace Designer
          </Typography>
          <Chip
            label={workspace?.title || 'New'}
            size="small"
            sx={{ bgcolor: '#8b5cf620', color: '#8b5cf6', fontWeight: 600 }}
          />
          <Chip
            label={workspace?.workspaceCode || ''}
            size="small"
            variant="outlined"
            sx={{ color: colors.textMuted, borderColor: colors.border, fontSize: '0.7rem' }}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            sx={{
              minHeight: 32,
              '& .MuiTab-root': { minHeight: 32, py: 0, textTransform: 'none', color: colors.textSecondary },
              '& .Mui-selected': { color: '#8b5cf6' },
              '& .MuiTabs-indicator': { bgcolor: '#8b5cf6' },
            }}
          >
            <Tab icon={<PreviewIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Designer" />
            <Tab icon={<CodeIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="JSON" />
          </Tabs>
          <Button
            size="small"
            variant="contained"
            startIcon={saving ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            sx={{ bgcolor: '#8b5cf6', textTransform: 'none', borderRadius: 0, '&:hover': { bgcolor: '#7c3aed' } }}
          >
            Save
          </Button>
          <IconButton size="small" onClick={onClose} sx={{ color: colors.textMuted }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Alerts */}
      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mx: 2, mt: 1 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mx: 2, mt: 1 }}>{success}</Alert>}

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {activeTab === 0 ? (
          /* ─── Designer Tab ─── */
          <Box sx={{ p: 2 }}>
            {/* Workspace-level settings */}
            <Paper sx={{ p: 2, mb: 2, bgcolor: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 0 }}>
              <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 600, mb: 1 }}>
                Workspace Settings
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  size="small"
                  label="Title"
                  value={definition.title || workspace?.title || ''}
                  onChange={(e) => setDefinition((prev) => ({ ...prev, title: e.target.value }))}
                  sx={{ flex: 1 }}
                  InputProps={{ sx: { color: colors.textPrimary, borderRadius: 0 } }}
                  InputLabelProps={{ sx: { color: colors.textSecondary } }}
                />
                <TextField
                  size="small"
                  label="Icon (MUI icon name)"
                  value={definition.icon || ''}
                  onChange={(e) => setDefinition((prev) => ({ ...prev, icon: e.target.value }))}
                  sx={{ flex: 1 }}
                  InputProps={{ sx: { color: colors.textPrimary, borderRadius: 0 } }}
                  InputLabelProps={{ sx: { color: colors.textSecondary } }}
                  helperText="e.g., Warehouse, MoveToInbox, LocalShipping"
                />
              </Box>
            </Paper>

            {/* Views */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
                Views ({definition.views?.length || 0})
              </Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={addView}
                sx={{ color: '#8b5cf6', textTransform: 'none' }}
              >
                Add View
              </Button>
            </Box>

            {(definition.views || []).map((view, idx) => (
              <Box key={idx} sx={{ position: 'relative' }}>
                {/* Move buttons */}
                <Box sx={{ position: 'absolute', left: -28, top: 8, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                  {idx > 0 && (
                    <IconButton size="small" onClick={() => moveView(idx, 'up')} sx={{ p: 0.25, color: colors.textMuted }}>
                      <UpIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  )}
                  {idx < (definition.views?.length || 0) - 1 && (
                    <IconButton size="small" onClick={() => moveView(idx, 'down')} sx={{ p: 0.25, color: colors.textMuted }}>
                      <DownIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  )}
                </Box>
                <ViewEditor
                  view={view}
                  index={idx}
                  onChange={(updated) => updateView(idx, updated)}
                  onDelete={() => deleteView(idx)}
                  colors={colors}
                />
              </Box>
            ))}

            {(!definition.views || definition.views.length === 0) && (
              <Box
                sx={{
                  p: 4, textAlign: 'center', border: `2px dashed ${colors.border}`,
                  bgcolor: colors.surface, cursor: 'pointer',
                }}
                onClick={addView}
              >
                <AddIcon sx={{ fontSize: 32, color: colors.textMuted, mb: 1 }} />
                <Typography variant="body2" sx={{ color: colors.textMuted }}>
                  Click to add a view
                </Typography>
              </Box>
            )}
          </Box>
        ) : (
          /* ─── JSON Preview Tab ─── */
          <Box sx={{ height: '100%', minHeight: 400 }}>
            <Editor
              language="json"
              theme="vs-dark"
              value={jsonPreview}
              onChange={(value) => {
                try {
                  const parsed = JSON.parse(value || '{}');
                  setDefinition(parsed);
                } catch {
                  // Don't update if invalid JSON
                }
              }}
              options={{
                minimap: { enabled: false },
                fontSize: 13,
                lineNumbers: 'on',
                folding: true,
                formatOnPaste: true,
                scrollBeyondLastLine: false,
                wordWrap: 'on',
                tabSize: 2,
                automaticLayout: true,
                readOnly: false,
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default WorkspaceDesigner;
